import React from 'react'
import { Button, useTheme } from '@mui/material'
import { format } from 'date-fns'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { LinkButton } from '@e3dc-react/shell/Elements/Buttons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ApiTableColumn, FetchProps } from '@e3dc-react/shell/Elements/ApiView/ApiView'
import { UseFetchProps } from '@e3dc-react/shell/Hooks/useFetch'
import { J2Multiply, J2Validate } from '@e3dc-react/icons'
import { useResendReferalCode } from './use-resend-referal-code'
import { AmazonCodeDto } from '../apis/dto/amazon-codes.dto'

/**
 * This hook holds all data for the referal codes api view
 */
export const useReferalCodesTableData = (): {
  fetchProps: UseFetchProps
  columns: ApiTableColumn<AmazonCodeDto>[]
} => {
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { t } = useCustomTranslation()
  const theme = useTheme()

  const { mutate: resendReferalCode } = useResendReferalCode({
    onSuccess: () => {
      enqueueSuccessStack(t('referal-codes.table.resend.success-message'))
    },
    onError: () => {
      enqueueErrorStack(t('referal-codes.table.resend.error-message'))
    },
  })

  const fetchProps: FetchProps = {
    url: `users/c2c-amazon-codes`,
    orderBy: '-id',
  }

  const emptySymbol = '-'

  const columns: ApiTableColumn<AmazonCodeDto>[] = [
    {
      name: 'createdAt',
      label: t('referal-codes.table.created'),
      value: ({ createdAt }) => (createdAt ? format(new Date(createdAt), t('referal-codes.table.datetime-format')) : emptySymbol),
    },
    {
      name: 'codeUsed',
      label: t('referal-codes.table.used'),
      value: ({ codeUsed }) => (codeUsed ? <J2Validate sx={{ color: 'success.main' }} /> : <J2Multiply sx={{ color: 'error.main' }} />),
    },
    {
      name: 'userId',
      label: t('referal-codes.table.user'),
      value: ({ userFullName, userId }) => {
        if (!userId || !userFullName) return emptySymbol

        return <LinkButton hasUnderline={false} linkColor={theme.palette.primary.main} content={userFullName} link={`/overview/users/${userId}`} />
      },
    },
    {
      name: 'referralCode',
      label: t('referal-codes.table.code'),
      value: ({ referralCode }) => referralCode ?? emptySymbol,
    },
    {
      name: 'sendAt',
      label: t('referal-codes.table.sendAt'),
      value: ({ sendAt }) => (sendAt ? format(new Date(sendAt), t('referal-codes.table.datetime-format')) : emptySymbol),
      sortable: false,
    },
    {
      name: 'amazonCode',
      label: t('referal-codes.table.amazonCode'),
      value: ({ amazonCode }) => amazonCode ?? emptySymbol,
      sortable: false,
    },
    {
      name: 'sendAt',
      label: '',
      value: ({ sendAt, id }) =>
        sendAt && (
          <Button onClick={() => resendReferalCode({ referalCodeId: id })} sx={{ whiteSpace: 'nowrap' }}>
            {t('referal-codes.table.resend.button-title')}
          </Button>
        ),
      sortable: false,
    },
  ]

  return { columns, fetchProps }
}
